import { Injectable, inject, signal, computed } from "@angular/core";
import { injectQuery, injectQueryClient, queryOptions } from "@ngneat/query";
import { derivedAsync } from "ngxtension/derived-async";
import moment from "moment";
import { ServiceOrderDataService } from "./services/service-order-data.service";
import {
  OrderServiceApiResponse,
  OrderServiceResponse,
  ParsedServiceDetail,
} from "@features/order-detail/models/order-services.model";
import {
  TenantUrlUtilService,
  DocumentType,
} from "@shared/services/tenant-url-util.service";

type QueryType = "order-id" | "document" | "os-id";

interface ServiceQueryParams {
  tenantId: string;
  documentValue?: string;
  documentType?: QueryType;
  osId?: string;
}

@Injectable({ providedIn: "root" })
export class ServicesOrderState {
  private readonly dataService = inject(ServiceOrderDataService);
  private readonly tenantUrlUtil = inject(TenantUrlUtilService);
  private readonly query = injectQuery();
  private readonly queryClient = injectQueryClient();

  readonly tenantId = computed(() => this.tenantUrlUtil.currentTenant());
  readonly documentValue = computed(
    () => this.tenantUrlUtil.documentValue() || "",
  );
  readonly documentType = computed(() => this.tenantUrlUtil.documentType());
  readonly manualSearchValue = signal<string>("");
  readonly selectedIndex = signal<number>(0);

  private readonly _params = computed<ServiceQueryParams | null>(() => {
    const tenant = this.tenantId();
    const manualValue = this.manualSearchValue();
    const urlDocumentValue = this.documentValue();

    if (!tenant) {
      return null;
    }

    const hasManual = !!manualValue;
    const hasUrlDocument = !!urlDocumentValue;

    if (!hasManual && !hasUrlDocument) {
      return null;
    }

    const params: ServiceQueryParams = { tenantId: tenant };

    if (hasUrlDocument) {
      const type = this.mapDocumentTypeToQueryType(this.documentType());
      params.documentValue = urlDocumentValue;
      params.documentType = type;
    }
    if (hasManual) {
      params.osId = manualValue;
    }
    return params;
  });

  private mapDocumentTypeToQueryType(docType: DocumentType): QueryType {
    switch (docType) {
      case "ORDER_ID":
      case "SALE_NOTE_NUMBER":
        return "order-id";
      case "IDENTITY_DOCUMENT":
      case "EMAIL":
        return "document";
      default:
        return "os-id";
    }
  }

  private getQueryOptions(params: ServiceQueryParams | null) {
    const tenantId = params?.tenantId ?? "";
    const documentValue = params?.documentValue;
    const documentType = params?.documentType;
    const osId = params?.osId;

    return queryOptions({
      queryKey: [
        "services",
        tenantId,
        documentValue,
        documentType,
        osId,
      ] as const,
      queryFn: async () => {
        return await this.dataService.fetchWithMultipleParams(
          tenantId,
          documentValue,
          documentType,
          osId,
        );
      },
    });
  }

  readonly servicesQuery = derivedAsync(() => {
    const params = this._params();
    return this.query({
      ...this.getQueryOptions(params),
      enabled:
        !!params?.tenantId && (!!params?.documentValue || !!params?.osId),
    }).result$;
  });

  readonly rawData = computed(() => {
    const result = this.servicesQuery();
    const data = result?.data as OrderServiceApiResponse[] | undefined;
    return data && data.length > 0 ? data : null;
  });

  readonly parsedData = computed(() => {
    const raw = this.rawData();
    if (!raw || raw.length === 0) {
      return null;
    }

    const enriched = raw.map((service) => {
      return {
        ...service,
        fechaRegistro: this.parseDateOnly(service.fechaRegistro)!,
        fechaEstimada: this.parseDateOnly(service.fechaEstimada)!,
        fechaRealizada: this.parseDateOnly(service.fechaRealizada)!,
        fechaReprogramada: this.parseDateOnly(service.fechaReprogramada),
        fechaConfirmacionInicio: this.parseDateOnly(
          service.fechaConfirmacionInicio,
        ),
        fechaConfirmacionFin: this.parseDateOnly(service.fechaConfirmacionFin),
        systemDateTime: this.parseDateTime(service.systemDateTime)!,
        tracking: (service?.tracking ?? []).map((t) => ({
          ...t,
          created_at: this.parseDateTime(t.created_at)!,
        })),
        servicios: (service.servicios ?? []).map((s) => ({
          ...s,
          tracking: (s.tracking ?? []).map((t) => ({
            ...t,
            created_at: this.parseDateTime(t.created_at)!,
            estimated_at: this.parseDateTime(t.estimated_at ?? null),
          })),
          lastTracking: s.lastTracking
            ? {
                ...s.lastTracking,
                created_at: this.parseDateTime(s.lastTracking.created_at)!,
                estimated_at: this.parseDateTime(s.lastTracking.estimated_at ?? null),
              }
            : undefined,
        })),
      };
    });

    enriched.sort((a, b) => {
      const dateA = a.fechaRegistro?.getTime() ?? 0;
      const dateB = b.fechaRegistro?.getTime() ?? 0;
      return dateB - dateA;
    });
    return enriched ?? null;
  });

  readonly loading = computed(() => {
    const result = this.servicesQuery();
    return result?.isLoading ?? false;
  });

  readonly error = computed(() => {
    const result = this.servicesQuery();
    return result?.error
      ? "No se pudo cargar la información de servicios"
      : null;
  });

  readonly canRetry = computed(() => {
    return !!this.error();
  });

  readonly notFound = computed(() => {
    const result = this.servicesQuery();
    return !this.loading() && !this.error() && this.rawData() === null;
  });

  readonly isLoaded = computed(
    () => !this.loading() && !this.error() && this.parsedData() !== null,
  );

  readonly isLoader = computed(
    () => this.loading() && !this.error() && this.parsedData() === null,
  );

  readonly isError = computed(() => !!this.error());

  readonly showNoResults = computed(() => this.notFound() && !this.loading());

  readonly isIdle = computed(
    () =>
      !this.loading() &&
      !this.error() &&
      this._params() === null &&
      this.rawData() === null,
  );

  readonly selectedService = computed(() => {
    const data = this.parsedData();
    const index = this.selectedIndex();

    if (!data || data.length === 0) {
      return null;
    }

    if (index >= 0 && index < data.length) {
      return data[index];
    }

    return data[0];
  });

  setSelectedIndex(index: number) {
    this.selectedIndex.set(index);
  }

  fetchByOrderId(tenantId: string, orderId: string) {
    this.manualSearchValue.set(orderId);
  }

  fetchByDocument(tenantId: string, document: string) {
    this.manualSearchValue.set(document);
  }

  fetchByOsId(tenantId: string, osId: string) {
    this.manualSearchValue.set(osId);
  }

  retry() {
    const params = this._params();
    if (params?.osId) {
      this.manualSearchValue.set(params.osId);
    }
  }

  reset() {
    this.manualSearchValue.set("");
  }

  prefetch(params: ServiceQueryParams) {
    this.queryClient.prefetchQuery(this.getQueryOptions(params));
  }

  invalidateServices() {
    this.queryClient.invalidateQueries({
      queryKey: ["services"],
    });
  }

  setManualSearchValue(value: string) {
    if (value) {
      this.manualSearchValue.set(value);
    }
  }

  getServiceTrackingDate(service: ParsedServiceDetail): Date | null {
    const canceledStates = ['Cancelado', 'CANCELED', 'Anulada', 'ANULADA'];

    if (service.lastTracking && canceledStates.includes(service.lastTracking.state)) {
      return service.lastTracking.created_at ?? null;
    }

    if (!service.tracking || service.tracking.length === 0) return null;
    const cancelledEntry = service.tracking.find((t: ParsedServiceDetail['tracking'][0]) =>
      canceledStates.includes(t.state)
    );
    return cancelledEntry?.created_at ?? null;
  }

  getServiceTrackingObservation(service: ParsedServiceDetail): string {
    const canceledStates = ['Cancelado', 'CANCELED', 'Anulada', 'ANULADA'];

    if (service.lastTracking && canceledStates.includes(service.lastTracking.state)) {
      return service.lastTracking.observations ?? service.comentario ?? '-';
    }

    if (!service.tracking || service.tracking.length === 0) return '-';
    const cancelledEntry = service.tracking.find((t: ParsedServiceDetail['tracking'][0]) =>
      canceledStates.includes(t.state)
    );
    return cancelledEntry?.observations ?? service.comentario ?? '-';
  }

  private parseDateOnly(dateStr: string | null): Date | null {
    if (!dateStr) return null;
    const m = moment(dateStr, "DD/MM/YYYY", true);
    return m.isValid() ? m.toDate() : null;
  }

  private parseDateTime(dateStr: string | null): Date | null {
    if (!dateStr) return null;
    const m = moment(dateStr);
    return m.isValid() ? m.toDate() : null;
  }
}
